/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import React, { useCallback, useEffect, useRef } from 'react';
import cx from 'classnames';
import styles from './styles.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { UnitPrice } from './UnitPrice';
import { generateAriaLabelsForQtyStepper } from '../../utils/generateAriaLabelsForQtyStepper';
import { interpolateLabel } from '@/utils/labels/interpolateLabel';
import { formatPrice } from '@/utils/priceHelpers';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

const labelMap = {
  lbl: 'label_shelf_resultsList_price_totalPrice',
  lblDecrease: 'label_Decrease',
  lblIncrease: 'label_Increase',
  lblStepperAria: 'label_cart_item_qty_stepper_aria',
  lblStepperAriaFallback: 'label_cart_item_qty_stepper_aria_fallback',
  lblCartEach: 'label_cart_Each',
  lblCartLineItemMaximumQuantityReached: 'label_cart_lineItem_MaximumQuantityReached',
  lblQtyIs: 'label_cart_qty_is',
  lblTotalPriceIs: 'label_shelf_resultsList_price_totalPrice',
  lblQty: 'label_quantity',
};

export type OnInputChange = (value: number, dirty: boolean, hasError?: boolean) => void;
type Props = {
  count: number | string;
  maxLength?: number;
  onInputChange: OnInputChange;
  handleZeroInput?: () => Promise<void>;
  onIncrementDecrement: (count: number, requireAnalytics: boolean, showQtyError?: boolean) => void;
  hasError?: boolean;
  minQuantity?: number;
  disableIncrementBtn?: boolean;
  disabled?: boolean;
  maxQuantity?: number;
  productName: string;
  isMaxQtyReached: boolean;
  unitPrice?: number | null | string | undefined;
  hideMaxQuantityError?: boolean;
  rootWrapperClassName?: string;
  stepperWithUnitPriceClassName?: string;
  buttonGroupClassName?: string;
  hideUnitPrice?: boolean;
  allow0Input: boolean;
  ariaDescribedBy?: string;
};

const NUMBER_PATTERN = /\d/;

export const handleKeyDown = async (
  event: KeyboardEvent | React.KeyboardEvent<HTMLInputElement>,
  count: number | string,
  allow0Input: boolean,
  handleZeroInput?: () => Promise<void>,
) => {
  if (!event || event.key !== 'Enter') {
    return;
  }

  const numericCount = typeof count === 'string' ? Number(count) : count;
  const isZeroOrEmpty = numericCount === 0 || count === '';

  if (!isZeroOrEmpty || !allow0Input) {
    return;
  }

  await handleZeroInput?.();
};

export const handleOnBlur = async (
  count: string | number,
  handleZeroInput?: () => Promise<void>,
  allow0Input?: boolean,
  onInputChange?: OnInputChange,
) => {
  const numericCount = typeof count === 'string' ? Number(count) : count;
  const isZeroOrEmpty = numericCount === 0 || count === '';

  if (!isZeroOrEmpty) {
    return;
  }

  if (allow0Input) {
    await handleZeroInput?.();
    return;
  }

  onInputChange?.(1, true);
};

export const handleKeyPress = (
  event: React.ChangeEvent<HTMLInputElement>,
  onInputChange: OnInputChange,
  hasError: boolean,
  maxQuantity?: number,
  allow0Input?: boolean,
): void => {
  if (!event?.target) {
    return;
  }

  const { value } = event.target;

  if (maxQuantity === 0) {
    onInputChange(0, false, hasError);
    return;
  }

  const valueTest = NUMBER_PATTERN.test(value);
  if (!valueTest && value !== '0' && value !== '') {
    return;
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return;
  }

  if (!allow0Input && numericValue === 0 && value !== '') {
    onInputChange(1, false, hasError);
    return;
  }

  onInputChange(numericValue, false, hasError);
};

/**
 * Calculates the total price based on quantity and unit price
 */
const calculateTotalPrice = (
  quantity: number | string,
  unitPrice?: number | null | string,
): number => {
  if (unitPrice == null) {
    return 0;
  }

  const numericQuantity = Number(quantity);
  const numericUnitPrice = Number(unitPrice);

  if (!Number.isFinite(numericQuantity) || !Number.isFinite(numericUnitPrice)) {
    return 0;
  }

  return numericQuantity * numericUnitPrice;
};

export const CalmBoxStepper = (props: Props) => {
  const {
    onIncrementDecrement,
    count,
    minQuantity = 1,
    hasError = false,
    onInputChange,
    maxLength = 2,
    disableIncrementBtn = false,
    handleZeroInput,
    maxQuantity,
    disabled = false,
    productName,
    isMaxQtyReached,
    unitPrice,
    hideMaxQuantityError,
    hideUnitPrice,
    rootWrapperClassName,
    stepperWithUnitPriceClassName,
    buttonGroupClassName,
    allow0Input = false,
    ariaDescribedBy,
  } = props;

  const labels = useLabels(labelMap);
  const statusRef = useRef<HTMLDivElement>(null);
  const shouldAnnounceRef = useRef<boolean>(false);
  const announcementSource = useRef<'input' | 'button'>('input');
  const lastAnnouncedCountRef = useRef<number | string | undefined>(undefined);
  const isProductDetailsPageV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');

  const regionId = `quantity-region-${React.useId()}`;
  const statusId = `quantity-status-${React.useId()}`;
  const errorId = `quantity-error-${React.useId()}`;
  const inputId = `quantity-input-${React.useId()}`;
  const qtyLabelId = `quantity-label-${React.useId()}`;

  const isZeroInputWired = Boolean(handleZeroInput) && allow0Input;
  const effectiveMinQuantity = isZeroInputWired ? 0 : minQuantity;

  // Keep useCallback - required for stable reference in useEffect dependency array
  // React Compiler optimizes component renders but doesn't replace hook dependency stability
  const createAnnouncement = useCallback(
    (qty: number, totalPrice: number, source: 'input' | 'button') => {
      const totalPriceLabel = `${labels.lblTotalPriceIs} ${formatPrice(
        totalPrice,
      )}`.toLocaleLowerCase();
      if (source === 'input') {
        return totalPriceLabel;
      }
      const quantityLabel = interpolateLabel(labels.lblQtyIs, {
        quantity: count,
      });
      return `${quantityLabel}, ${totalPriceLabel}`;
    },
    [labels.lblTotalPriceIs, labels.lblQtyIs, count],
  );

  const handleIncrementWithStatus = (newCount: number) => {
    shouldAnnounceRef.current = true;
    announcementSource.current = 'button';
    onIncrementDecrement(newCount, true, hasError);
  };

  const handleDecrementWithStatus = (newCount: number) => {
    const finalCount = !isZeroInputWired && newCount === 0 ? 1 : newCount;
    shouldAnnounceRef.current = true;
    announcementSource.current = 'button';
    onIncrementDecrement(finalCount, true, hasError);
  };

  const handleInputChangeWithStatus = (value: number, dirty: boolean, hasError?: boolean) => {
    const finalValue = !isZeroInputWired && value === 0 ? 1 : value;
    if (dirty) {
      announcementSource.current = 'input';
      shouldAnnounceRef.current = true;
    }
    onInputChange(finalValue, dirty, hasError);
  };

  /**
   * Handles input change events for the quantity input field with status announcements.
   * Validates numeric input and updates the quantity value while managing error states.
   * Allows 0 as a valid input value when allow0Input is true and handleZeroInput is provided.
   * Note: Entering "0" will only remove the item on blur or Enter key press if allow0Input is true.
   *
   * @param event - The input change event containing the new value
   */
  const handleKeyPressWithStatus = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!event || !event.target) {
      return;
    }

    const valueTest = NUMBER_PATTERN.test(event.target.value);
    if (maxQuantity !== null && maxQuantity !== undefined && maxQuantity === 0) {
      handleInputChangeWithStatus(0, false, hasError);
      return;
    }
    if (valueTest || event.target.value === '0' || event.target.value === '') {
      if (!Number.isNaN(Number(event.target.value))) {
        const inputValue = Number(event.target.value);

        if (!isZeroInputWired && inputValue === 0 && event.target.value !== '') {
          handleInputChangeWithStatus(1, true, hasError);
        } else {
          handleInputChangeWithStatus(inputValue, true, hasError);
        }
      }
    }
  };

  const checkIfDecrementDisabled = (): boolean => {
    const countLessThanMin = Number(count) <= minQuantity;
    let storeHasNoQoh: boolean = false;
    if (maxQuantity !== undefined) {
      storeHasNoQoh = maxQuantity <= 0;
    }
    return countLessThanMin || storeHasNoQoh;
  };

  /**
   * Handles keyboard events for spinbutton functionality
   * Implements proper keyboard navigation for accessibility
   * Uses handleInputChangeWithStatus for input-based interactions
   * Allows keyboard navigation to reach 0 when allow0Input is true and handleZeroInput is available
   */
  const handleSpinbuttonKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValue = Number(count);
    const step = 1;
    const largeStep = 10;

    const spinbuttonKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'];
    if (spinbuttonKeys.includes(event.key)) {
      event.preventDefault();
    }

    switch (event.key) {
      case 'ArrowUp':
        if (!disabled && !disableIncrementBtn) {
          const newValue = Math.min(currentValue + step, maxQuantity || Number.MAX_SAFE_INTEGER);
          if (newValue !== currentValue) {
            handleInputChangeWithStatus(newValue, true, hasError);
          }
        }
        break;

      case 'ArrowDown':
        if (!disabled) {
          let newValue = Math.max(currentValue - step, effectiveMinQuantity);
          if (!allow0Input && newValue === 0) {
            newValue = 1;
          }
          if (newValue !== currentValue) {
            handleInputChangeWithStatus(newValue, true, hasError);
          }
        }
        break;

      case 'PageUp':
        if (!disabled && !disableIncrementBtn) {
          const newValue = Math.min(
            currentValue + largeStep,
            maxQuantity || Number.MAX_SAFE_INTEGER,
          );
          if (newValue !== currentValue) {
            handleInputChangeWithStatus(newValue, true, hasError);
          }
        }
        break;

      case 'PageDown':
        if (!disabled) {
          let newValue = Math.max(currentValue - largeStep, effectiveMinQuantity);
          if (!allow0Input && newValue === 0) {
            newValue = 1;
          }
          if (newValue !== currentValue) {
            handleInputChangeWithStatus(newValue, true, hasError);
          }
        }
        break;

      case 'Home':
        if (!disabled) {
          let homeValue = effectiveMinQuantity;
          if (!isZeroInputWired && homeValue === 0) {
            homeValue = 1;
          }
          if (currentValue !== homeValue) {
            handleInputChangeWithStatus(homeValue, true, hasError);
          }
        }
        break;

      case 'End':
        if (!disabled && !disableIncrementBtn && maxQuantity && currentValue !== maxQuantity) {
          handleInputChangeWithStatus(maxQuantity, true, hasError);
        }
        break;

      case 'Enter':
        void handleKeyDown(event, count, isZeroInputWired, handleZeroInput);
        break;

      default:
        break;
    }
  };

  const decrementAmount: number = maxQuantity
    ? Number(count) > maxQuantity
      ? maxQuantity
      : Number(count) - 1
    : Number(count) - 1;

  const showError = Boolean(isMaxQtyReached) && !hideMaxQuantityError;
  const showEach = Boolean(unitPrice && Number(count) > 1) && !hideUnitPrice;
  const isDecrementDisabled = checkIfDecrementDisabled();

  const incrementAriaLabel = generateAriaLabelsForQtyStepper({
    action: labels.lblIncrease,
    template: labels.lblStepperAria,
    quantity: count,
    productName: productName,
    variant: 'concise',
  });

  const decrementAriaLabel = generateAriaLabelsForQtyStepper({
    action: labels.lblDecrease,
    template: labels.lblStepperAria,
    quantity: count,
    productName: productName,
    variant: 'concise',
  });

  useEffect(() => {
    if (!statusRef?.current) {
      return;
    }

    const isInitialRender = lastAnnouncedCountRef.current === undefined;

    if (!isInitialRender && !shouldAnnounceRef.current) {
      return;
    }

    if (!isInitialRender && lastAnnouncedCountRef.current === count) {
      shouldAnnounceRef.current = false;
      return;
    }

    const message = createAnnouncement(
      Number(count),
      calculateTotalPrice(count, unitPrice),
      announcementSource.current,
    );

    statusRef.current.textContent = '';
    requestAnimationFrame(() => {
      if (statusRef.current) {
        statusRef.current.textContent = message;
        lastAnnouncedCountRef.current = count;
        shouldAnnounceRef.current = false;
      }
    });
  }, [count, createAnnouncement, unitPrice]);

  return (
    <div className={cx(styles.wrapper, rootWrapperClassName)}>
      {/* Live region for status announcements */}
      <div
        ref={statusRef}
        aria-live="polite"
        aria-atomic="true"
        id={statusId}
        className={styles.srOnly}
      />

      <div id={regionId} className={cx(styles.stepperWithEach, stepperWithUnitPriceClassName)}>
        {/* Accessible quantity label (screen reader only) */}
        <span id={qtyLabelId} className={styles.srOnly}>
          {labels.lblQty}
        </span>

        <div
          className={cx(styles.container, disabled && styles.isDisabled, buttonGroupClassName, {
            [styles.tallContainer]: isProductDetailsPageV2Enabled,
          })}
          role="region"
          aria-labelledby={qtyLabelId}
        >
          <button
            type="button"
            data-testid="quantity-decrement"
            disabled={isDecrementDisabled || disabled}
            className={[styles.button, styles.buttonMinus].filter(Boolean).join(' ')}
            aria-label={decrementAriaLabel}
            onClick={() => handleDecrementWithStatus(decrementAmount)}
            aria-controls={inputId}
          >
            −
          </button>
          <input
            id={inputId}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            tabIndex={0}
            className={styles.valueInput}
            value={String(count)}
            disabled={disabled}
            onChange={handleKeyPressWithStatus}
            onKeyDown={handleSpinbuttonKeyDown}
            onBlur={() => handleOnBlur(count, handleZeroInput, isZeroInputWired, onInputChange)}
            maxLength={maxLength}
            role="spinbutton"
            aria-valuenow={Number(count)}
            aria-valuemin={effectiveMinQuantity}
            aria-valuemax={maxQuantity != null ? Number(maxQuantity) : undefined}
            aria-labelledby={qtyLabelId}
            aria-invalid={hasError || showError ? true : undefined}
            aria-describedby={
              [showError ? errorId : undefined, ariaDescribedBy].filter(Boolean).join(' ') ||
              undefined
            }
            aria-errormessage={showError ? errorId : undefined}
          />
          <button
            data-testid="quantity-increment"
            type="button"
            className={[styles.button, styles.buttonPlus].filter(Boolean).join(' ')}
            aria-label={incrementAriaLabel}
            onClick={() => handleIncrementWithStatus(Number(count) + 1)}
            disabled={disableIncrementBtn || disabled}
            aria-controls={inputId}
          >
            +
          </button>
        </div>

        <UnitPrice show={showEach} unitPrice={unitPrice} />
      </div>
    </div>
  );
};

export default CalmBoxStepper;
